// b2b-users-apiservice.js
import { config } from '../config';
import { generateQueryString, isValidUUID, makeApiRequest_AppJson, makeApiRequest_Text, makeApiRequest_formData } from './common-apiService';

// USERS

export const fetchBasicUserDataByUUID = async (userUUID, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/basicInfo`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchUserDataByUUID = async (userUUID, loadFactor = null, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/loadFactor`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, loadFactor);
};

export const deleteUserDataByUUID = async (userUUID, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/delete`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
};

export const fetchGitUserData = async () => {
    const url = `${config.baseB2B_URL}/b2b/git`;
    const headers = {
        'X-B2B-API-Key': config.gitAPIKey,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchAllUsersData = async (accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/getAll`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchAllUsersWithMembershipCount_Paged = async (page, pageSize, searchFields, accessToken) => {
    const queryString = generateQueryString(searchFields);
    const url = `${config.baseB2B_URL}/b2b/users/getMembershipCount/paged?page=${page}&size=${pageSize}${queryString ? `&${queryString}` : ''}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchUserOrganizations = async (userUUID, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/organizations`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchAllRoles = async (accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/roles`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const enableDisableUser = async (userUUID, enableUser, accessToken) => {
    if (!isValidUUID(userUUID)) {
        throw new Error('Invalid userUUID format');
    }
    const url = enableUser ?
        `${config.baseB2B_URL}/b2b/users/${userUUID}/enable` :
        `${config.baseB2B_URL}/b2b/users/${userUUID}/disable`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers);
}

export const newUser = async (newUserDTO, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/new`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...newUserDTO,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
}

export const updateUser = async (userUUID, updatedUserDTO, accessToken) => {
    if (!isValidUUID(userUUID)) {
        throw new Error('Invalid userUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/update`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...updatedUserDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const updateLoggedUser = async (updatedUserDTO, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/update`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...updatedUserDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const changeLoggedUserPassword = async (currentPassword, newPassword, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/changePassword`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        oldPassword: currentPassword,
        newPassword,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
}

export const getUserMembershipsPaged = async (userUUID, page = 0, pageSize = 10, searchFields, accessToken) => {
    const queryString = generateQueryString(searchFields);
    const url = `${config.baseB2B_URL}/b2b/users/${userUUID}/organizations/paged?page=${page}&size=${pageSize}${queryString ? `&${queryString}` : ''}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}

// ORGANIZATION

export const blindLogin = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/users/${organizationUUID}/blindLogin`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers);
}

export const fetchMyOrganizationsData = async (accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/myOrganizations`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}

export const fetchOrganizationsData = async (accessToken, getMembers = false) => {
    const url = `${config.baseB2B_URL}/b2b/organizations${getMembers ? '?getMembers=true' : ''}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchOrganizationsWithMemberAndLicenseCountAndActiveStatusData = async (page = 0, pageSize = 10, searchFields, accessToken, blacklist = null) => {
    const queryString = generateQueryString(searchFields);
    const url = `${config.baseB2B_URL}/b2b/organizations/getMemberAndLicenseCountAndActiveStatus/paged?page=${page}&size=${pageSize}${queryString ? `&${queryString}` : ''}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const requestBody = {
        blacklist: blacklist
    };
    //console.log('requestBody', requestBody)
    return makeApiRequest_AppJson(url, 'POST', headers, requestBody);
};

export const fetchOrganizationDataByUUID = async (organizationUUID, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const fetchOrganizationRoles = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/roles`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const createOrganization = async (organizationName, accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/new`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        organizationBack2BluType: 'SYS_SUSTAINABLE_ENTITY',
        organizationName,
        organizationEnabled: false,
        parentOrganizationUUID: null,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
};

export const updateOrganization = async (organizationUUID, updatedOrgDTO, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/basicInfo`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...updatedOrgDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const enableDisableOrganization = async (organizationUUID, enableOrganization, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = enableOrganization ?
        `${config.baseB2B_URL}/b2b/${organizationUUID}/enable` :
        `${config.baseB2B_URL}/b2b/${organizationUUID}/disable`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers);
}

export const uploadOrganizationImage = async (organizationUUID, file, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const formData = new FormData();
    formData.append("image", file);
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/addImage`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_formData(url, 'POST', headers, formData);
}

export const getOrganizationImagePath = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/getImagePath`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_Text(url, 'GET', headers);
}

export const getOrganizationImagePathWithApiKey = async (organizationUUID) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/imagePath`;
    const headers = {
        'X-B2B-API-Key': config.b2bUsersAPIKey,
    };
    return makeApiRequest_Text(url, 'GET', headers);
}

export const downloadOrganizationImage = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/getImage`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    try {
        const response = await makeApiRequest_formData(url, 'GET', headers);

        if (response.status === 200) {
            const blob = await response.blob();
            const dataUrl = URL.createObjectURL(blob);
            return dataUrl;
        } else {
            console.error('Error downloading organization image', response);
            throw new Error(`Failed to download organization image: ${response.status} - ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error in downloadOrganizationImage:', error);
        throw new Error('Error downloading organization image');
    }
}

export const downloadOrganizationImageWithApiKey = async (organizationUUID) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/image`;
    const headers = {
        'X-B2B-API-Key': config.b2bUsersAPIKey,
    };
    try {
        const response = await makeApiRequest_formData(url, 'GET', headers);

        if (response.status === 200) {
            const blob = await response.blob();
            const dataUrl = URL.createObjectURL(blob);
            return dataUrl;
        } else {
            console.error('Error downloading organization image', response);
            throw new Error(`Failed to download organization image: ${response.status} - ${response.statusText}`);
        }
    }
    catch (error) {
        console.error('Error in downloadOrganizationImageWithApiKey:', error);
        throw new Error('Error downloading organization image');
    }
}

export const getOrganizationNameWithApiKey = async (organizationUUID) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/name`;
    const headers = {
        'X-B2B-API-Key': config.b2bUsersAPIKey,
    };
    return makeApiRequest_Text(url, 'GET', headers);
}

export const deleteOrganization = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/delete?propagate=true`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
}

export const cloneOrganization = async (organizationUUID, accessToken, cloneContent = false) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/clone?cloneContent=${cloneContent}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers);
}

// PRODUCTS & LICENSES

export const fetchProductsData = async (accessToken) => {
    const url = `${config.baseB2B_URL}/b2b/products?getParent=false`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
};

export const addProduct = async (accessToken, productDTO) => {
    const url = `${config.baseB2B_URL}/b2b/products/new`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...productDTO,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
}

export const updateProduct = async (accessToken, productDTO) => {
    const url = `${config.baseB2B_URL}/b2b/products/${productDTO.productId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...productDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const uploadProductImage = async (accessToken, productId, file) => {
    const url = `${config.baseB2B_URL}/b2b/products/${productId}/image`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const formData = new FormData();
    formData.append("image", file);
    return makeApiRequest_formData(url, 'POST', headers, formData);
}

export const removeProductImage = async (accessToken, productId) => {
    const url = `${config.baseB2B_URL}/b2b/products/${productId}/image`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
}

export const deleteProduct = async(accessToken, productId) => {
    const url = `${config.baseB2B_URL}/b2b/products/${productId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
}

export const enableDisableOrganizationLicense = async (organizationUUID, licenseId, enableLicense, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = enableLicense ?
        `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/${licenseId}/enable` :
        `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/${licenseId}/disable`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers);
}

export const updateOrganizationLicense = async (organizationUUID, licenseId, licenseDTO, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/${licenseId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...licenseDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const createOrganizationLicense = async (organizationUUID, licenseDTO, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/new`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...licenseDTO,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
}

export const removeOrganizationLicense = async (organizationUUID, licenseId, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/${licenseId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
}

export const fetchOrganizationActiveLicense = async (organizationUUID, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/licenses/active`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}

// MEMBERSHIP

export const enableDisableUserMembership = async (organizationUUID, userId, enableUserMembership, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = enableUserMembership ?
        `${config.baseB2B_URL}/b2b/${organizationUUID}/users/${userId}/enable` :
        `${config.baseB2B_URL}/b2b/${organizationUUID}/users/${userId}/disable`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers);
}

export const updateUserMembership = async (organizationUUID, userId, orgMemberDTO, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/${userId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...orgMemberDTO,
    };
    return makeApiRequest_AppJson(url, 'PUT', headers, body);
}

export const createUserMembership = async (organizationUUID, userPayload, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/new`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...userPayload,
    };
    return makeApiRequest_AppJson(url, 'POST', headers, body);
}

export const createUserMembershipByUserId = async (organizationUUID, userId, roleName, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/${userId}?userRole=${encodeURIComponent(roleName)}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers);
}

export const createUserMembershipByEmail = async (organizationUUID, email, roleName, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/addUserEmail?userEmail=${encodeURIComponent(email)}&userRole=${encodeURIComponent(roleName)}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers);
}

export const removeUserMembership = async (organizationUUID, userId, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/${userId}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'DELETE', headers);
}

export const inviteMemberByEmail = async (organizationUUID, email, roleName, expirationTime, accessToken) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/invite?userEmail=${encodeURIComponent(email)}&userRole=${encodeURIComponent(roleName)}&tokenTimeout=${expirationTime}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'POST', headers);
}

export const fetchUserFromInviteToken = async (inviteToken) => {
    const url = `${config.baseB2B_URL}/b2b/users/inviteToken/${inviteToken}`;
    return makeApiRequest_AppJson(url, 'GET');
}

export const newUserInvited = async (inviteToken, organizationUUID, newUserDTO) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/newFromInvite/${inviteToken}`;
    const body = {
        ...newUserDTO,
    };
    return makeApiRequest_AppJson(url, 'POST', {}, body);
}

export const fetchUserByUserUUID = async (accessToken, organizationUUID, userUUID) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    if (!isValidUUID(userUUID)) {
        throw new Error('Invalid userUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/byUUID/${userUUID}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}

export const fetchUsersHavingRoles = async (accessToken, organizationUUID, roleNames) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    if (!Array.isArray(roleNames) || roleNames.some(role => typeof role !== 'string')) {
        throw new Error('roleNames must be an array of strings');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/havingRoles?roles=${roleNames.join(',')}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}

export const fetchOrganizationUsersPaged = async (accessToken, organizationUUID, page = 0, pageSize = 10) => {
    if (!isValidUUID(organizationUUID)) {
        throw new Error('Invalid organizationUUID format');
    }
    const url = `${config.baseB2B_URL}/b2b/${organizationUUID}/users/paged?page=${page}&size=${pageSize}`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };
    return makeApiRequest_AppJson(url, 'GET', headers);
}